/**
 * Frosted glass chrome surface (HeaderBar, SearchBar).
 * `getFrostedGlassStyle` is the material; `FrostedGlassGlow` adds the soft web colour blobs.
 */
import React, { memo } from 'react';
import { Platform, View } from 'react-native';

export const FROSTED_GLASS_BLUR = 20;

export function getFrostedGlassColors(theme: boolean) {
  return {
    backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    borderColor: theme ? '#e5e7eb' : '#374151',
  };
}

/**
 * Background, border colour and web blur for a glass surface.
 * Apply to the element that should blur what is behind it: a web ancestor with
 * `mask-image`, `filter` or `backdrop-filter` stops descendants from blurring the page.
 */
export function getFrostedGlassStyle(theme: boolean) {
  const { backgroundColor, borderColor } = getFrostedGlassColors(theme);
  return {
    backgroundColor,
    borderColor,
    ...(Platform.OS === 'web' &&
      ({
        backdropFilter: `blur(${FROSTED_GLASS_BLUR}px)`,
        WebkitBackdropFilter: `blur(${FROSTED_GLASS_BLUR}px)`,
        boxShadow: `0 1px 0 0 ${borderColor}`,
      } as object)),
  };
}

type FrostedGlassGlowProps = {
  theme: boolean;
  /** Clip the blobs to the surface (use when the parent can overflow). */
  clip?: boolean;
  borderRadius?: number;
};

/** Decorative blurred colour blobs; web only. Render as the first child of a glass surface. */
export const FrostedGlassGlow = memo(function FrostedGlassGlow({
  theme,
  clip = false,
  borderRadius,
}: FrostedGlassGlowProps) {
  if (Platform.OS !== 'web') return null;
  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        ...(clip && { overflow: 'hidden' as const }),
        ...(borderRadius != null && { borderRadius }),
      }}
    >
      <View
        style={{
          position: 'absolute',
          top: '10%',
          left: '12%',
          width: 100,
          height: 100,
          backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
          borderRadius: 50,
          filter: 'blur(40px)',
        } as any}
      />
      <View
        style={{
          position: 'absolute',
          bottom: '-40%',
          right: '18%',
          width: 120,
          height: 120,
          backgroundColor: theme ? 'rgba(200, 162, 200, 0.25)' : 'rgba(135, 206, 235, 0.2)',
          borderRadius: 60,
          filter: 'blur(50px)',
        } as any}
      />
    </View>
  );
});
